import type { CreatedCart, PosCartCustomer } from '@/lib/services/carts/carts.types'
import type { OrderQuoteCart } from '@/lib/order-quote'
import type { CustomerReceivablesTone, PayLaterProjection } from '@/lib/pos/customer-receivables'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { CustomerTypeBadge } from '@/components/customers/CustomerTypeBadge'
import { formatPhone } from '@/hooks/use-phone-formatter'
import {
  customerQueries,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} from '@/lib/services/customers/customers.query'
import { getCustomerReceivablesContext } from '@/lib/pos/customer-receivables'
import { warnMissingOrderQuoteFinancials } from '@/lib/order-quote'
import { formatCustomerAddressLine } from '@/lib/services/customers/customers.address'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  AlertTriangle,
  CircleDollarSign,
  Mail,
  MapPin,
  Pencil,
  Phone,
  UserPlus,
  UserRoundPen,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { cn, formatCurrency, getErrorMessage } from '@/lib/utils'
import { CustomerModal } from './CustomerModal'
import { useUpdateCartMutation, useIsCartMutating } from '@/lib/services/carts/carts.query'
import { useAbility } from '@/lib/casl'
import { usePathHelper } from '@/hooks/usePathHelper'
import { CustomerSearchInput, renderCustomerOption } from './CustomerSearchInput'

const CUSTOMER_LOOKUP_LIMIT = 10

function getBillingContextToneClass(tone: CustomerReceivablesTone) {
  return {
    danger: 'text-status-destructive-foreground dark:text-status-destructive-foreground',
    warning: 'text-status-warning-foreground dark:text-status-warning-foreground',
    neutral: 'text-muted-foreground',
  }[tone]
}

type CustomerCart = OrderQuoteCart & {
  id: number
  purchaser?: PosCartCustomer | null
  ordering_contact_id?: number | null
}

type CustomerSectionProps = {
  cart: CustomerCart | null | undefined
  createCartWithDefaults: () => Promise<CreatedCart>
  disabled?: boolean
  /** What Pay Later would leave on the customer's account, as the server projects it for the cart or the change. */
  payLater?: PayLaterProjection | null
  /** An order change's draft: an order keeps a customer, so staff change it rather than take it off. */
  isOrderChange?: boolean
}

export function CustomerSection({
  cart,
  createCartWithDefaults,
  disabled = false,
  payLater = null,
  isOrderChange = false,
}: CustomerSectionProps) {
  const [searchTerm, setSearchTerm] = useState('')
  // Update Customer edits the cart's customer; Add New Customer creates one, even while the cart still has the customer
  // being replaced in an order change.
  const [customerModal, setCustomerModal] = useState<'update' | 'create' | null>(null)
  // The customer staff are replacing in an order change. It stays on the order until another is picked, and the search
  // closes by itself once one is.
  const [replacingCustomerId, setReplacingCustomerId] = useState<number | null>(null)
  const queryClient = useQueryClient()
  const orgId = useRouteOrganizationId()
  const ability = useAbility()
  const path = usePathHelper()

  const updateCartMutation = useUpdateCartMutation()
  const createCustomerMutation = useCreateCustomerMutation()
  const updateCustomerMutation = useUpdateCustomerMutation()
  const isCartMutating = useIsCartMutating()

  const isMutationPending =
    createCustomerMutation.isPending ||
    updateCustomerMutation.isPending ||
    isCartMutating > 0 ||
    disabled

  const selectedCustomer = cart?.purchaser
  const isReplacingCustomer =
    Boolean(selectedCustomer) && replacingCustomerId === selectedCustomer?.id
  const selectedCustomerAddress = formatCustomerAddressLine(selectedCustomer?.physical_address)
  const selectedContacts = Array.isArray(selectedCustomer?.contacts)
    ? selectedCustomer.contacts
    : []
  const canSelectOrderingContact =
    Boolean(selectedCustomer?.is_business) && selectedContacts.length > 0
  warnMissingOrderQuoteFinancials(
    cart,
    ['total', 'remainingTotal'],
    'POS customer billing context is missing order_quote totals.'
  )
  const {
    openBalance,
    overdueBalance,
    creditLimit,
    projectedOpenBalance,
    isProjectedOverCreditLimit,
    balanceTone,
  } = getCustomerReceivablesContext({
    customer: selectedCustomer,
    projection: payLater,
  })
  const billingContextItems = (
    [
      openBalance > 0
        ? {
            label: 'Open balance',
            value: `${formatCurrency(openBalance)} open`,
            tone: balanceTone,
          }
        : null,
      overdueBalance > 0
        ? {
            label: 'Past due',
            value: formatCurrency(overdueBalance),
            tone: balanceTone,
          }
        : null,
      creditLimit !== null
        ? { label: 'Credit limit', value: formatCurrency(creditLimit), tone: 'neutral' }
        : null,
      isProjectedOverCreditLimit
        ? {
            label: isOrderChange ? 'After this change' : 'After this order',
            value: formatCurrency(projectedOpenBalance),
            tone: 'danger',
          }
        : null,
    ] satisfies ({ label: string; value: string; tone: CustomerReceivablesTone } | null)[]
  ).filter((item) => item !== null)
  const canCreateCustomer = ability.can('create', 'customers')
  const canUpdateCustomer = ability.can('edit', 'customers')

  const fetchCustomerOptions = (searchInput: string) =>
    queryClient.fetchQuery(
      customerQueries.index({
        organizationId: orgId,
        filters: {
          autocomplete: true,
          limit: CUSTOMER_LOOKUP_LIMIT,
          search: searchInput,
        },
      })
    )

  const handleCustomerSelect = async (customer: Pick<PosCartCustomer, 'id'>) => {
    // A cart that fails to be created reports like a failed update, rather than doing nothing.
    try {
      const cartId = cart?.id ?? (await createCartWithDefaults()).id
      await updateCartMutation.mutateAsync({
        cartId,
        data: { purchaser_customer_id: customer.id },
      })
      setSearchTerm('')
    } catch (error) {
      // The search stays, so staff can pick the customer again.
      toast.error('Failed to update cart', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleCustomerRemove = async () => {
    if (!cart) return
    try {
      await updateCartMutation.mutateAsync({
        cartId: cart?.id,
        data: { purchaser_customer_id: null },
      })
    } catch (error) {
      toast.error('Failed to remove customer', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleOrderingContactChange = async (value: string) => {
    if (!cart?.id) {
      return
    }

    try {
      await updateCartMutation.mutateAsync({
        cartId: cart.id,
        data: { ordering_contact_id: value === 'none' ? null : Number(value) },
      })
    } catch (error) {
      toast.error('Failed to update ordering contact', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  return (
    <div className="mb-4 space-y-2">
      <Typography variant="label" tone="muted">
        Customer
      </Typography>
      {selectedCustomer && !isReplacingCustomer ? (
        <div className="space-y-2">
          <div className="rounded-lg bg-muted/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div
                  className="flex min-w-0 items-center gap-2"
                  data-testid="selected-customer-name-row"
                >
                  <Link
                    {...path.customers.show(selectedCustomer.id).link}
                    className="block min-w-0 truncate rounded-sm text-sm font-semibold text-foreground hover:text-interaction focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {selectedCustomer.name}
                  </Link>
                  <CustomerTypeBadge
                    isBusiness={Boolean(selectedCustomer.is_business)}
                    className="shrink-0"
                  />
                </div>
                {selectedCustomer.phone || selectedCustomer.email || selectedCustomerAddress ? (
                  <div className="mt-1.5 grid gap-0.5" data-testid="selected-customer-contact-rows">
                    {selectedCustomer.phone ? (
                      <Typography variant="label" tone="muted" className="block">
                        {formatPhone(selectedCustomer.phone)}
                      </Typography>
                    ) : null}
                    {selectedCustomer.email ? (
                      <Typography variant="label" tone="muted" className="block">
                        {selectedCustomer.email}
                      </Typography>
                    ) : null}
                    {selectedCustomerAddress ? (
                      <Typography variant="label" tone="muted" className="block">
                        {selectedCustomerAddress}
                      </Typography>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {canUpdateCustomer ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => setCustomerModal('update')}
                    disabled={isMutationPending}
                    aria-label="Update Customer"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                ) : null}
                {isOrderChange ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => setReplacingCustomerId(selectedCustomer.id)}
                    disabled={isMutationPending}
                    aria-label="Change Customer"
                  >
                    <UserRoundPen className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={handleCustomerRemove}
                    disabled={isMutationPending}
                    aria-label="Remove Customer"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {billingContextItems.length > 0 ? (
              <div className="mt-3 grid gap-1.5 border-t border-border/60 pt-3">
                {billingContextItems.map((item) => (
                  <div
                    key={item.label}
                    className={cn(
                      'flex items-center justify-between gap-2 text-xs',
                      getBillingContextToneClass(item.tone)
                    )}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {item.tone === 'danger' || item.tone === 'warning' ? (
                        <AlertTriangle className="size-3.5" />
                      ) : (
                        <CircleDollarSign className="size-3.5" />
                      )}
                      {item.label}
                    </span>
                    <span className="font-medium tabular-nums">{item.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          {canSelectOrderingContact ? (
            <div className="space-y-1.5">
              <Typography variant="label" tone="muted">
                Ordering Contact
              </Typography>
              <Select
                value={cart?.ordering_contact_id ? String(cart.ordering_contact_id) : 'none'}
                onValueChange={handleOrderingContactChange}
                disabled={isMutationPending}
              >
                <SelectTrigger size="toolbar" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact selected</SelectItem>
                  {selectedContacts.map((contact) => (
                    <SelectItem key={contact.id} value={String(contact.id)}>
                      {contact.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          <CustomerSearchInput
            id="customer-search"
            placeholder="Search by phone number, name, or email..."
            value={searchTerm}
            onChange={setSearchTerm}
            fetchOptions={fetchCustomerOptions}
            onOptionSelected={handleCustomerSelect}
            renderOption={renderCustomerOption}
            disabled={isMutationPending}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setCustomerModal('create')}
            disabled={isMutationPending || !canCreateCustomer}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add New Customer
          </Button>
          {isReplacingCustomer ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setReplacingCustomerId(null)}
              disabled={isMutationPending}
            >
              Keep {selectedCustomer?.name}
            </Button>
          ) : null}
        </div>
      )}

      {customerModal && (
        <CustomerModal
          open
          onOpenChange={(open: boolean) => {
            if (!open) setCustomerModal(null)
          }}
          cart={customerModal === 'create' && cart ? { ...cart, purchaser: null } : cart}
          createCartWithDefaults={createCartWithDefaults}
          initialSearchQuery={searchTerm}
        />
      )}
    </div>
  )
}
